import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Trash2,
  Copy,
  ExternalLink,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DnsInstruction {
  type: "A" | "CNAME" | "TXT";
  name: string;
  value: string;
}

interface CustomDomain {
  id: string;
  domain: string;
  status: "pending" | "active" | "error";
  dns_instructions: DnsInstruction[] | null;
  verified_at: string | null;
  created_at: string;
}

interface CustomDomainPanelProps {
  projectId: string;
  /** Show only if the project has been deployed at least once */
  hasDeployed?: boolean;
}

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string)?.replace(/\/$/, "");
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export function CustomDomainPanel({ projectId, hasDeployed = true }: CustomDomainPanelProps) {
  const [domainInput, setDomainInput] = useState("");
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load domains from database
  const loadDomains = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from("custom_domains")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });

      if (fetchError) {
        console.error("[CustomDomainPanel] Load error:", fetchError);
        return;
      }

      setDomains((data as CustomDomain[]) || []);
    } catch (err) {
      console.error("[CustomDomainPanel] Load error:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const callEdgeFunction = async (
    action: "add" | "verify" | "remove",
    domain?: string
  ): Promise<{ success?: boolean; error?: string; [key: string]: any }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { error: "Session expired, please sign in again" };
    }

    const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-custom-domain`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ action, projectId, domain }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { error: data.error || `Request failed (${res.status})` };
    }

    return data;
  };

  const handleAddDomain = async () => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain) {
      toast.error("Enter a domain name");
      return;
    }

    // Basic validation
    if (!/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(domain)) {
      toast.error("Enter a valid domain (e.g., myapp.com)");
      return;
    }

    setActionLoading("add");
    setError(null);

    const result = await callEdgeFunction("add", domain);

    if (result.error) {
      setError(result.error);
      toast.error(result.error);
    } else {
      toast.success("Domain added! Configure your DNS settings below.");
      setDomainInput("");
      await loadDomains();
    }

    setActionLoading(null);
  };

  const handleVerifyDomain = async (domain: string) => {
    setActionLoading(`verify-${domain}`);
    setError(null);

    const result = await callEdgeFunction("verify", domain);

    if (result.error) {
      setError(result.error);
      toast.error(result.error);
    } else if (result.active) {
      toast.success("Domain verified and active!");
      await loadDomains();
    } else {
      toast.info("DNS not configured yet. Check your settings.");
      await loadDomains();
    }

    setActionLoading(null);
  };

  const handleRemoveDomain = async (domain: string) => {
    if (!confirm(`Remove ${domain} from this project?`)) {
      return;
    }

    setActionLoading(`remove-${domain}`);
    setError(null);

    const result = await callEdgeFunction("remove", domain);

    if (result.error) {
      setError(result.error);
      toast.error(result.error);
    } else {
      toast.success("Domain removed");
      await loadDomains();
    }

    setActionLoading(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (!hasDeployed) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>Deploy your app first to add a custom domain</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="h-4 w-4 text-primary" />
        <Label className="text-sm font-medium">Custom Domain</Label>
      </div>

      {/* Add domain input */}
      <div className="flex gap-2">
        <Input
          placeholder="Enter your domain (e.g., myapp.com)"
          value={domainInput}
          onChange={(e) => setDomainInput(e.target.value)}
          className="h-9 text-sm"
          onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
        />
        <Button
          size="sm"
          onClick={handleAddDomain}
          disabled={actionLoading === "add" || !domainInput.trim()}
          className="h-9 shrink-0"
        >
          {actionLoading === "add" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Connect Domain"
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Domain list */}
      {domains.length > 0 && (
        <div className="space-y-3">
          {domains.map((d) => (
            <div
              key={d.id}
              className="rounded-lg border bg-card p-3 space-y-3"
            >
              {/* Domain header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {d.status === "active" ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : d.status === "error" ? (
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  ) : (
                    <div className="h-4 w-4 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                  )}
                  {d.status === "active" ? (
                    <a
                      href={`https://${d.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                    >
                      {d.domain}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-sm font-medium">{d.domain}</span>
                  )}
                  <Badge
                    variant={d.status === "active" ? "default" : d.status === "error" ? "destructive" : "secondary"}
                    className="text-[10px] h-5"
                  >
                    {d.status === "active" ? "Active" : d.status === "error" ? "Error" : "Pending"}
                  </Badge>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleVerifyDomain(d.domain)}
                    disabled={actionLoading === `verify-${d.domain}`}
                    className="h-7 w-7 p-0"
                    title="Check verification status"
                  >
                    {actionLoading === `verify-${d.domain}` ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveDomain(d.domain)}
                    disabled={actionLoading === `remove-${d.domain}`}
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    title="Remove domain"
                  >
                    {actionLoading === `remove-${d.domain}` ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>

              {/* DNS instructions */}
              {d.status !== "active" && d.dns_instructions && d.dns_instructions.length > 0 && (
                <div className="rounded bg-zinc-950 p-3 text-xs font-mono space-y-2">
                  <p className="text-zinc-400 text-[10px] uppercase tracking-wide">
                    DNS Configuration Required
                  </p>
                  <div className="space-y-1.5">
                    {d.dns_instructions.map((dns, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-zinc-900/50"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className="text-[10px] h-5 shrink-0">
                            {dns.type}
                          </Badge>
                          <span className="text-zinc-400">{dns.name}</span>
                          <span className="text-zinc-600">→</span>
                          <span className="text-green-400 truncate">{dns.value}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(dns.value)}
                          className="h-6 w-6 p-0 shrink-0"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <p className="text-zinc-500 text-[10px] mt-2">
                    Add these records to your domain's DNS settings, then click the refresh button to verify.
                  </p>
                </div>
              )}

              {d.status === "active" && d.verified_at && (
                <p className="text-xs text-muted-foreground">
                  Verified {new Date(d.verified_at).toLocaleDateString()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {domains.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-4">
          No custom domains configured. Add one above to use your own domain.
        </p>
      )}
    </div>
  );
}
